import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { api, endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, Mic2, Building2, Megaphone, Disc3, Info } from "lucide-react";
import { postAuthDestination, normalizeIntent } from "@/lib/postAuthRouting";

const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const BUILD_TIME_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// UI hint only. Server-side onboarding_state is the source of truth once it
// exists. Backend does NOT flip user.role from "fan" — workspace access is
// carried by artist_members / venue_members rows.
const INTENT_STORAGE_KEY = "pm_signup_intent";
const INTENTS = [
    { id: "fan", label: "Fan", blurb: "Follow artists, register for events, carry your Passport.", Icon: Users, enabled: true },
    { id: "artist", label: "Artist", blurb: "Manage your profile, events, team, and audience.", Icon: Mic2, enabled: true },
    { id: "venue", label: "Venue", blurb: "Program events and grow your audience.", Icon: Building2, enabled: true },
    { id: "promoter", label: "Promoter", blurb: "Book artists across venues and grow an audience.", Icon: Megaphone, enabled: true },
    { id: "label", label: "Label", blurb: "Represent a roster and coordinate shows.", Icon: Disc3, enabled: true },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
async function customGoogleRedirect(next, clientId, intent) {
    const state = window.crypto?.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, "")
        : Math.random().toString(36).slice(2) + Date.now().toString(36);

    // Durable server-side intent — survives Apple ITP, third-party cookie
    // resets, and any sessionStorage loss across the Google round-trip.
    // Best-effort; sessionStorage remains a same-tab fallback.
    if (intent) {
        try {
            await api.post("/auth/oauth-intent", {
                state,
                intent,
                next: next && next.startsWith("/") ? next : "",
            });
        } catch { /* fall back to sessionStorage below */ }
    }
    try {
        if (next) sessionStorage.setItem("pm_oauth_next", next);
        else sessionStorage.removeItem("pm_oauth_next");
        sessionStorage.setItem("pm_oauth_state", state);
        if (intent) sessionStorage.setItem(INTENT_STORAGE_KEY, intent);
        else sessionStorage.removeItem(INTENT_STORAGE_KEY);
    } catch {}
    const redirect = window.location.origin + "/auth/google";
    const url = `${GOOGLE_AUTH_URL}?client_id=${encodeURIComponent(clientId)}`
        + `&redirect_uri=${encodeURIComponent(redirect)}`
        + `&response_type=code`
        + `&scope=${encodeURIComponent("openid email profile")}`
        + `&state=${encodeURIComponent(state)}`
        + `&prompt=select_account`
        + `&access_type=online`
        + `&include_granted_scopes=true`;
    window.location.href = url;
}

export default function SignUp() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const nextParam = params.get("next") || "";
    const { register, user } = useAuth();
    const [intent, setIntent] = useState(() => {
        try { return normalizeIntent(sessionStorage.getItem(INTENT_STORAGE_KEY)) || "fan"; } catch { return "fan"; }
    });
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [googleEnabled, setGoogleEnabled] = useState(false);
    const [googleClientId, setGoogleClientId] = useState("");

    // Track whether THIS SignUp render just completed a registration. If not,
    // do NOT auto-redirect on user population — that lets someone who is
    // already signed in intentionally revisit /signup (e.g. from a shared
    // link) without being bounced away based on stale intent.
    const justRegisteredRef = useRef(false);

    // Keep sessionStorage in lockstep with the picked intent so it survives
    // the Google redirect. (Server-side oauth-intent is written just before
    // the redirect fires — this key is only a same-tab convenience.)
    useEffect(() => {
        try { sessionStorage.setItem(INTENT_STORAGE_KEY, intent); } catch {}
    }, [intent]);

    // If the user is already authenticated when they hit /signup, route them
    // using the SAME rules as post-auth routing — but only when they have
    // no explicit intent to pick. This prevents the historical race where
    // `user.role === "fan"` silently sent Artist-choosers to Fan territory.
    useEffect(() => {
        if (justRegisteredRef.current) return; // handled explicitly by submit
        if (!user || typeof user !== "object") return;
        // Preserve the user's intent-picker choice for the routing helper.
        (async () => {
            const dest = await postAuthDestination({
                explicitNext: nextParam,
                intent: intent === "fan" ? "" : intent,
            });
            nav(dest, { replace: true });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, nextParam]);

    useEffect(() => {
        endpoints.authGoogleConfig()
            .then((d) => {
                const cid = (d && d.client_id) || BUILD_TIME_GOOGLE_CLIENT_ID;
                if (d && d.enabled && cid) {
                    setGoogleClientId(cid);
                    setGoogleEnabled(true);
                } else {
                    setGoogleEnabled(false);
                }
            })
            .catch(() => setGoogleEnabled(false));
    }, []);

    const artistHint = useMemo(() => intent === "artist", [intent]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        setBusy(true);
        setError("");
        justRegisteredRef.current = true;
        try {
            await register({ name, email, password, intent });
            // Persist server-side onboarding intent BEFORE navigating so
            // that any downstream logic (WorkspaceGate, ArtistSetup resume,
            // cross-device login) sees the correct state. This is the
            // ordering fix for the production regression where Artist
            // intent could be lost before /onboarding/artist mounted.
            if (intent === "artist") {
                try {
                    await endpoints.upsertOnboardingState({ intent: "artist", status: "started" });
                } catch (e2) {
                    // Non-fatal: ArtistSetup will retry via its own upsert on mount.
                    // eslint-disable-next-line no-console
                    console.warn("[signup] artist onboarding-state upsert failed:", e2?.message || e2);
                }
            }
            toast.success("Welcome to Project Mint");
            const dest = await postAuthDestination({
                explicitNext: nextParam,
                intent: intent === "fan" ? "" : intent,
            });
            nav(dest, { replace: true });
        } catch (err) {
            justRegisteredRef.current = false;
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const onGoogle = async () => {
        if (!googleEnabled || !googleClientId) {
            toast.error("Google sign-in is temporarily unavailable. Please sign up with your email.");
            return;
        }
        // Preserve intent through the Google redirect using durable server
        // storage (see /api/auth/oauth-intent). This eliminates the class
        // of bugs where sessionStorage vanished between the redirect out
        // and the redirect back on iOS Safari / privacy-hardened browsers.
        const preferredNext = nextParam || (intent === "artist" ? "/onboarding/artist" : "");
        await customGoogleRedirect(preferredNext, googleClientId, intent);
    };

    return (
        <div className="max-w-md mx-auto px-4 md:px-6 py-16" data-testid="signup-page">
            <div className="border border-[#2a2a2a] p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
                <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl">Create account</h1>
                <ul className="mt-3 space-y-2 text-sm text-[#a3a3a3]" data-testid="signup-intro-copy">
                    <li className="flex flex-col sm:flex-row sm:gap-3">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#f4f4f0] sm:w-32 sm:shrink-0 sm:pt-[3px]">Fans</span>
                        <span>Follow artists and venues, save and register for events, and access your Passport.</span>
                    </li>
                    <li className="flex flex-col sm:flex-row sm:gap-3">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#f4f4f0] sm:w-32 sm:shrink-0 sm:pt-[3px]">Artists & Venues</span>
                        <span>Manage your profile, events, team, check-in, and audience.</span>
                    </li>
                </ul>

                <div className="mt-6" data-testid="signup-intent-picker">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-2">I'm here as…</div>
                    <div className="grid grid-cols-2 gap-2">
                        {INTENTS.map((it) => {
                            const active = intent === it.id;
                            return (
                                <button
                                    key={it.id}
                                    type="button"
                                    disabled={!it.enabled}
                                    onClick={() => it.enabled && setIntent(it.id)}
                                    aria-pressed={active}
                                    className={`text-left border p-3 transition-colors ${
                                        active
                                            ? "border-[#ff4400] bg-[#ff4400]/10"
                                            : it.enabled
                                                ? "border-[#2a2a2a] hover:border-[#666]"
                                                : "border-[#1a1a1a] opacity-40 cursor-not-allowed"
                                    }`}
                                    data-testid={`signup-intent-${it.id}`}
                                    title={it.enabled ? "" : "Onboarding coming soon"}
                                >
                                    <it.Icon className={`w-4 h-4 ${active ? "text-[#ff4400]" : "text-[#a3a3a3]"}`} />
                                    <div className={`mt-2 font-display font-bold uppercase text-xs ${active ? "text-[#ff4400]" : "text-[#f4f4f0]"}`}>
                                        {it.label}
                                    </div>
                                    {!it.enabled && (
                                        <div className="mt-0.5 text-[9px] font-mono uppercase text-[#666]" data-testid={`signup-intent-${it.id}-coming-soon`}>
                                            Coming soon
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-2 text-[10px] font-mono uppercase text-[#666] flex items-start gap-1" data-testid="signup-intent-hint">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>One universal Project Mint account — you can wear multiple hats.</span>
                    </div>
                    {artistHint && (
                        <div className="mt-2 text-[10px] font-mono uppercase text-[#ff4400]" data-testid="signup-artist-next-hint">
                            Next: set up your Artist profile.
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={onGoogle}
                    disabled={!googleEnabled}
                    className="pm-btn w-full mt-6 flex items-center justify-center gap-2 border-[#f4f4f0] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="signup-google-btn"
                    data-google-flow={googleEnabled ? "custom" : "disabled"}
                    data-signup-intent={intent}
                >
                    Continue with Google
                </button>

                <div className="mt-6 flex items-center gap-3 text-[10px] font-mono uppercase text-[#666]">
                    <div className="flex-1 h-px bg-[#2a2a2a]" /> or <div className="flex-1 h-px bg-[#2a2a2a]" />
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <Field label="Name" value={name} onChange={setName} autoComplete="name" testId="signup-name" />
                    <Field label="Email" value={email} onChange={setEmail} type="email" autoComplete="email" testId="signup-email" />
                    <Field label="Password (min 8)" value={password} onChange={setPassword} type="password" autoComplete="new-password" testId="signup-password" />
                    {error && <div className="text-xs font-mono uppercase text-[#ff3333]" data-testid="signup-error">{error}</div>}
                    <button
                        type="submit"
                        disabled={busy || !name || !email || !password}
                        className="pm-btn pm-btn-primary w-full mt-2"
                        data-testid="signup-submit"
                        data-signup-intent={intent}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Create account
                    </button>
                </form>

                <div className="mt-6 text-center text-xs font-mono uppercase">
                    <Link to={`/signin${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} className="text-[#a3a3a3] hover:text-[#ff4400]" data-testid="signup-signin-link">
                        Already have an account? Sign in →
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId, autoComplete }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">{label}</span>
            <input
                type={type}
                autoComplete={autoComplete}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-[#f4f4f0]"
                data-testid={testId}
            />
        </label>
    );
}
